import { i18n } from "@lingui/core";
import { plural } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import type { AiConsentStatus } from "@orbin/contracts";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@orbin/ui-web";
import { useState } from "react";
import type { SharingRecipient } from "../lib/metering";
import { rpc } from "../lib/rpc";
import { errorText } from "../lib/user-error";

/** "A", "A and B", "A, B and C" in the interface language, each name once. */
function listNames(names: readonly string[]): string {
  return new Intl.ListFormat(i18n.locale || "en", { style: "long", type: "conjunction" }).format([
    ...new Set(names),
  ]);
}

/** The refusal to share data with a recipient, with the review that lets the user allow it. */
export function AiDataSharingPrompt({
  recipients,
  onAllowed,
}: {
  recipients: SharingRecipient[];
  onAllowed?: () => void;
}) {
  const { t } = useLingui();
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<AiConsentStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The dialog lists every partner still refused and allows exactly those, so a grant never covers a partner the user did not see.
  const denied = status ? status.recipients.filter((r) => !r.allowed) : [];

  async function review() {
    setOpen(true);
    setError(null);
    try {
      setStatus(await rpc.aiConsent.status({}));
    } catch (cause) {
      setError(errorText(cause, t`Could not load permissions.`));
    }
  }

  async function allow() {
    if (!status || busy || denied.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      await rpc.aiConsent.allow({
        scope: status.scope,
        version: status.version,
        keys: denied.map((r) => r.key),
      });
      setOpen(false);
      onAllowed?.();
    } catch (cause) {
      setError(errorText(cause, t`Could not save permissions.`));
    } finally {
      setBusy(false);
    }
  }

  const refusedCount = new Set(recipients.map((r) => r.name)).size;
  const names = listNames(recipients.map((r) => r.name));
  const sharedNames = listNames(denied.map((r) => r.name));
  const detailed = denied.filter((recipient) => recipient.detail);
  return (
    <span data-testid="ai-sharing-prompt" className="inline-flex flex-wrap items-center gap-x-2">
      <span>
        {refusedCount === 0
          ? t`Allow AI data sharing to continue.`
          : plural(refusedCount, {
              one: `Allow ${names} to receive your data to continue.`,
              other: `Allow ${names} to receive your data to continue.`,
            })}
      </span>
      <button
        type="button"
        data-testid="ai-sharing-review"
        onClick={() => void review()}
        className="font-medium underline underline-offset-2 hover:text-foreground"
      >
        {t`Review`}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="ai-sharing-dialog">
          <DialogHeader>
            <DialogTitle>{t`AI data sharing`}</DialogTitle>
            <DialogDescription className={denied.length === 0 ? "sr-only" : undefined}>
              {denied.length === 0
                ? t`Services that receive your data`
                : t`To continue, your data goes to ${sharedNames}.`}
            </DialogDescription>
          </DialogHeader>
          {detailed.length > 0 ? (
            <ul className="flex flex-col gap-1 text-sm text-muted-foreground">
              {detailed.map((recipient) => (
                <li key={recipient.key}>
                  {recipient.name}: {recipient.detail}
                </li>
              ))}
            </ul>
          ) : null}
          {status?.privacyUrl ? (
            <a
              href={status.privacyUrl}
              target="_blank"
              rel="noreferrer"
              className="text-sm text-muted-foreground underline underline-offset-2 hover:text-foreground"
            >
              {t`Privacy policy`}
            </a>
          ) : null}
          {error ? (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          ) : null}
          {/* With no partner left to allow, the send is retried: it checks again and asks afresh. */}
          {denied.length > 0 ? (
            <Button disabled={busy} onClick={() => void allow()}>
              {t`Allow`}
            </Button>
          ) : (
            <Button
              onClick={() => {
                setOpen(false);
                onAllowed?.();
              }}
            >
              {t`Try again`}
            </Button>
          )}
        </DialogContent>
      </Dialog>
    </span>
  );
}
